// Customer profile — update name + change password from inside the portal.
// Lives under /help/:slug/profile so customers can find it from the
// portal header without leaving their branded space.
import React, { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { usePortal } from './PortalLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

export default function PortalProfilePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { customer, reloadCustomer } = usePortal();
  const [name, setName] = useState(customer?.name || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  if (!customer) { navigate(`/help/${slug}/login`); return null; }

  const saveProfile = async (e) => {
    e?.preventDefault();
    setSavingProfile(true);
    try {
      await axios.patch(`${API}/customer/me`, { name: name.trim() }, { withCredentials: true });
      await reloadCustomer();
      toast.success('Profile updated');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
    } finally { setSavingProfile(false); }
  };

  const changePassword = async (e) => {
    e?.preventDefault();
    if (newPassword.length < 8) return toast.error('New password must be at least 8 characters');
    if (newPassword !== confirmPassword) return toast.error('Passwords do not match');
    setChangingPassword(true);
    try {
      await axios.patch(`${API}/customer/me`,
        { current_password: currentPassword, new_password: newPassword },
        { withCredentials: true });
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
      toast.success('Password changed');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
    } finally { setChangingPassword(false); }
  };

  return (
    <div className="max-w-2xl mx-auto" data-testid="portal-profile-page">
      <Link to={`/help/${slug}/requests`} className="text-xs text-zinc-500 hover:text-zinc-900">← Back to my requests</Link>
      <h1 className="text-xl font-bold text-zinc-900 font-['Outfit'] mt-3 mb-4">Profile</h1>

      <form onSubmit={saveProfile} className="bg-white border border-zinc-200 rounded-lg p-6 mb-4">
        <h2 className="text-sm font-semibold text-zinc-900 mb-3">Account details</h2>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Email (read-only)</Label>
            <Input value={customer.email} disabled className="bg-zinc-50" />
          </div>
          <div>
            <Label className="text-xs">Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="portal-profile-name" />
          </div>
          <div>
            <Button type="submit" size="sm" disabled={savingProfile} data-testid="portal-profile-save">
              {savingProfile ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </div>
      </form>

      <form onSubmit={changePassword} className="bg-white border border-zinc-200 rounded-lg p-6">
        <h2 className="text-sm font-semibold text-zinc-900 mb-3">Change password</h2>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Current password</Label>
            <Input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)}
              data-testid="portal-profile-current-password" />
          </div>
          <div>
            <Label className="text-xs">New password</Label>
            <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
              placeholder="At least 8 characters" data-testid="portal-profile-new-password" />
          </div>
          <div>
            <Label className="text-xs">Confirm new password</Label>
            <Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
              data-testid="portal-profile-confirm-password" />
          </div>
          <div>
            <Button type="submit" size="sm" disabled={changingPassword || !currentPassword || !newPassword}
              data-testid="portal-profile-change-password">
              {changingPassword ? 'Changing…' : 'Change password'}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
